import { useMemo } from 'react';

import { cx, Button, Card, Flex, Text } from '@endurancewarranty/ui';
import { useAppRouter } from '@hooks/navigation';
import { useContactRouter } from '@hooks/useContactRouter';
import { AppRouteEnum } from '@lib/enums/AppRouteEnum';
import { useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';

import type { FC } from 'react';

const ENTITY_LABELS = {
  Cart: 'Cart',
  Consumer: 'Customer',
  ConsumerVehicleId: 'Consumer vehicle',
  Contact: 'Contact',
  FinderCode: 'Finder code',
  Home: 'Home',
  Page: 'Page',
  Quote: 'Quote',
  Vehicle: 'Vehicle',
  Coverage: 'Coverage',
  Lead: 'Lead',
  MoxyCustomerNumber: 'Moxy customer number',
  ContractNumber: 'Contract',
} as const;

type Props = {
  className?: string;
  consumerId?: number;
  entity?: keyof typeof ENTITY_LABELS;
  header?: string;
  subHeader?: string;
  actions?: Action[];
};

type Action = {
  label: string;
  onClick: () => void;
};

export const NotFoundCard: FC<Props> = ({
  actions,
  className,
  consumerId,
  entity,
  header,
  subHeader,
}) => {
  const appRouter = useAppRouter();
  const { goToSearchPageForCurrentContact } = useContactRouter();
  const { contactId, channel } = useJotaiValue(contactSelector);

  const label = entity != null ? ENTITY_LABELS[entity] : null;

  const defaultActions = useMemo(() => {
    if (contactId != null && channel != null) {
      return [
        {
          label: 'Back to search',
          onClick: () => goToSearchPageForCurrentContact({ clearPreviousSearchResults: false }),
        },
      ];
    }
    if (consumerId != null) {
      return [
        {
          label: 'Back to profile',
          onClick: () => appRouter.build(AppRouteEnum.ConsumerProfile).setId(consumerId).go(),
        },
      ];
    }
    return [
      {
        label: 'Back to dashboard',
        onClick: () => appRouter.build(AppRouteEnum.Dashboard).go(),
      },
    ];
  }, [appRouter, channel, consumerId, contactId, goToSearchPageForCurrentContact]);

  return (
    <Card
      backgroundColor={100}
      borderColor={100}
      padding="p-6"
      className={cx('page-content-max-width', className)}>
      <Flex className="gap-32" direction="row" items="center" justify="between">
        <div>
          <Text.Header color={900} fontWeight="medium" size="xs">
            {header ?? `${label} not found`}
          </Text.Header>
          <Text.Body margin="mt-1" wordBreak={'break-word'}>
            {subHeader ?? `This ${label?.toLowerCase()} no longer exists in our system.`}
          </Text.Body>
        </div>
        <Flex className="gap-2" direction="row">
          {(actions ?? defaultActions).map((action, index) => (
            <div key={action.label} className="min-w-[108px]">
              <Button
                key={action.label}
                name={`not-found-action-${index}`}
                size="sm"
                variant="gradient"
                onClick={action.onClick}>
                {action.label}
              </Button>
            </div>
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};
